import config from 'lib/config'
import { getRefreshToken } from 'utils/auth'

export type BillingStatusResponse = {
  ok: boolean
  clientPid?: string
  clientId?: string | null
  plan?: string
  status?: string
  remainingDays?: number | null
  required?: boolean
  warn?: boolean
  trialEndsAt?: string | null
  billingProvider?: 'shopify' | 'stripe' | 'none'
  hasShopifyLink?: boolean
  confirmationUrl?: string | null
  organizationBillingUrl?: string | null
  error?: string
}

function sessionToken(): string | null {
  const pair = getRefreshToken()
  const token = pair[0]
  return token && String(token).trim() !== '' ? String(token).trim() : null
}

export async function fetchBillingStatus(): Promise<BillingStatusResponse | null> {
  const base = config.billingApiBase
  if (!base) return null
  const token = sessionToken()
  if (!token) return null
  const res = await fetch(`${base}/billing/v1/status`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  const body = (await res.json().catch(() => ({}))) as BillingStatusResponse
  if (!res.ok) {
    return { ok: false, error: body.error || `HTTP ${res.status}` }
  }
  return body
}

export async function startBillingSubscribe(): Promise<{
  ok: boolean
  confirmationUrl?: string
  organizationBillingUrl?: string
  error?: string
  code?: string
}> {
  const base = config.billingApiBase
  if (!base) return { ok: false, error: 'Billing API is not configured' }
  const token = sessionToken()
  if (!token) return { ok: false, error: 'Not signed in' }
  try {
    const res = await fetch(`${base}/billing/v1/subscribe`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean
      confirmationUrl?: string
      organizationBillingUrl?: string
      error?: string
      code?: string
    }
    if (!res.ok || !body.ok) {
      const code = body.code ? ` [${body.code}]` : ''
      return {
        ok: false,
        error: (body.error || `HTTP ${res.status}`) + code,
        code: body.code,
      }
    }
    return {
      ok: true,
      confirmationUrl: body.confirmationUrl,
      organizationBillingUrl: body.organizationBillingUrl,
    }
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Billing subscribe request failed',
    }
  }
}

/**
 * Persist App Pricing plan selection after Shopify redirects with plan_handle.
 * Returns updated status payload on success.
 */
export async function confirmShopifyPlan(params: {
  planHandle: string
  shop?: string
}): Promise<BillingStatusResponse> {
  const base = config.billingApiBase
  if (!base) return { ok: false, error: 'Billing API is not configured' }
  const token = sessionToken()
  if (!token) return { ok: false, error: 'Not signed in' }
  try {
    const res = await fetch(`${base}/billing/v1/confirm-shopify-plan`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        planHandle: params.planHandle,
        shop: params.shop || undefined,
      }),
    })
    const body = (await res.json().catch(() => ({}))) as BillingStatusResponse
    if (!res.ok) {
      return {
        ok: false,
        error: body.error || `HTTP ${res.status}`,
      }
    }
    return body
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Confirm plan request failed',
    }
  }
}

/** Open Select plan Admin URL — prefer status; fall back to subscribe. */
export async function openShopifySelectPlan(
  status: BillingStatusResponse | null
): Promise<{ ok: boolean; error?: string }> {
  if (!status || !status.ok || status.billingProvider !== 'shopify') {
    return { ok: false, error: 'Could not open Shopify plan selection' }
  }
  if (status.confirmationUrl) {
    window.location.href = status.confirmationUrl
    return { ok: true }
  }
  const result = await startBillingSubscribe()
  if (result.ok && result.confirmationUrl) {
    window.location.href = result.confirmationUrl
    return { ok: true }
  }
  return { ok: false, error: result.error || 'Could not open Shopify plan selection' }
}

/** Open View billing (organization billing) Admin URL. */
export async function openShopifyViewBilling(
  status: BillingStatusResponse | null
): Promise<{ ok: boolean; error?: string }> {
  if (!status || !status.ok || status.billingProvider !== 'shopify') {
    return { ok: false, error: 'Could not open Shopify organization billing' }
  }
  if (status.organizationBillingUrl) {
    window.location.href = status.organizationBillingUrl
    return { ok: true }
  }
  const result = await startBillingSubscribe()
  if (result.ok && result.organizationBillingUrl) {
    window.location.href = result.organizationBillingUrl
    return { ok: true }
  }
  return { ok: false, error: result.error || 'Could not open Shopify organization billing' }
}

export async function startStripeCheckout(params: {
  plan: string
  interval: 'month' | 'year'
}): Promise<{
  ok: boolean
  url?: string
  error?: string
  code?: string
}> {
  const base = config.billingApiBase
  if (!base) return { ok: false, error: 'Billing API is not configured' }
  const token = sessionToken()
  if (!token) return { ok: false, error: 'Not signed in' }
  try {
    const res = await fetch(`${base}/billing/v1/stripe/checkout`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        plan: params.plan,
        interval: params.interval,
      }),
    })
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean
      url?: string
      error?: string
      code?: string
    }
    if (!res.ok || !body.ok || !body.url) {
      const code = body.code ? ` [${body.code}]` : ''
      return {
        ok: false,
        error: (body.error || `HTTP ${res.status}`) + code,
        code: body.code,
      }
    }
    window.location.href = body.url
    return { ok: true, url: body.url }
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Stripe checkout request failed',
    }
  }
}

export async function startStripePortal(): Promise<{
  ok: boolean
  url?: string
  error?: string
  code?: string
}> {
  const base = config.billingApiBase
  if (!base) return { ok: false, error: 'Billing API is not configured' }
  const token = sessionToken()
  if (!token) return { ok: false, error: 'Not signed in' }
  try {
    const res = await fetch(`${base}/billing/v1/stripe/portal`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean
      url?: string
      error?: string
      code?: string
    }
    if (!res.ok || !body.ok || !body.url) {
      const code = body.code ? ` [${body.code}]` : ''
      return {
        ok: false,
        error: (body.error || `HTTP ${res.status}`) + code,
        code: body.code,
      }
    }
    window.location.href = body.url
    return { ok: true, url: body.url }
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : 'Stripe portal request failed',
    }
  }
}

export type FeatureMode = 'classic' | 'new' | 'off'

export type FeatureEntitlementPlan = {
  source: 'assignment' | 'subscription' | 'default'
  id: number
  slug: string
  name: string
  upload: FeatureMode
  gallery: FeatureMode
  galleryUpload: FeatureMode
  catalogEdit: boolean
  galleryAi: boolean
  maxProducts: number | null
}

export async function fetchFeatureEntitlements(): Promise<FeatureEntitlementPlan | null> {
  const base = config.billingApiBase
  if (!base) return null
  const token = sessionToken()
  if (!token) return null
  try {
    const res = await fetch(`${base}/billing/v1/entitlements`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean
      source?: FeatureEntitlementPlan['source']
      plan?: Omit<FeatureEntitlementPlan, 'source'>
    }
    if (!res.ok || !body.ok || !body.plan || !body.source) return null
    return {
      source: body.source,
      id: body.plan.id,
      slug: body.plan.slug,
      name: body.plan.name,
      upload: body.plan.upload,
      gallery: body.plan.gallery,
      galleryUpload: body.plan.galleryUpload,
      catalogEdit: Boolean(body.plan.catalogEdit),
      galleryAi: Boolean(body.plan.galleryAi),
      maxProducts: body.plan.maxProducts == null ? null : Number(body.plan.maxProducts),
    }
  } catch {
    return null
  }
}
